import { useState, useCallback } from "react";
import { format } from "date-fns";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { X, ChevronRight } from "lucide-react";
import DateScrollPicker from "@/components/ui/date-scroll-picker";

const CalendarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" className="mr-2 flex-shrink-0">
    <rect x="1.5" y="3" width="15" height="13.5" rx="2" stroke="hsl(43 50% 54%)" strokeWidth="1.5" fill="none" />
    <line x1="1.5" y1="7.5" x2="16.5" y2="7.5" stroke="hsl(43 50% 54%)" strokeWidth="1.5" />
    <line x1="5.25" y1="1.5" x2="5.25" y2="4.5" stroke="hsl(43 50% 54%)" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="12.75" y1="1.5" x2="12.75" y2="4.5" stroke="hsl(43 50% 54%)" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

interface DatePickerFieldProps {
  value?: Date;
  onChange: (date: Date, yearSelected: boolean) => void;
  placeholder?: string;
  title?: string;
  yearOptional?: boolean;
  yearRequired?: boolean;
}

const DatePickerField = ({
  value,
  onChange,
  placeholder = "Select a date",
  title = "Date",
  yearOptional = false,
  yearRequired = false,
}: DatePickerFieldProps) => {
  const [open, setOpen] = useState(false);
  const [tempDate, setTempDate] = useState<Date | undefined>(value);
  const [tempYearSelected, setTempYearSelected] = useState(false);

  const handleOpen = () => {
    setTempDate(value);
    setTempYearSelected(false);
    setOpen(true);
  };

  const handlePickerChange = useCallback((date: Date, yearSelected: boolean) => {
    setTempDate(date);
    setTempYearSelected(yearSelected);
  }, []);

  const handleDone = () => {
    if (tempDate) {
      onChange(tempDate, tempYearSelected);
    }
    setOpen(false);
  };

  const formatDisplay = () => {
    if (!value) return null;
    const isAnyYear = value.getFullYear() === 2000;
    return isAnyYear ? format(value, "dd MMM") : format(value, "dd MMM yyyy");
  };

  const displayValue = formatDisplay();

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className="flex h-12 w-full items-center rounded-full border border-[hsl(37_25%_86%)] bg-white px-4 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-accent"
      >
        <CalendarIcon />
        <span
          className={
            displayValue
              ? "flex-1 text-base text-[hsl(0_0%_10%)]"
              : "flex-1 text-base text-[hsl(0_0%_60%)]"
          }
        >
          {displayValue || placeholder}
        </span>
        <ChevronRight className="h-4 w-4 text-[hsl(0_0%_60%)]" />
      </button>

      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent className="max-h-[85vh]">
          <div className="mx-auto w-full max-w-md px-6 pb-6">
            {/* Header */}
            <div className="flex items-center justify-between py-4">
              <h3 className="font-serif text-lg font-bold text-foreground">{title}</h3>
              <button
                onClick={() => setOpen(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Picker */}
            <DateScrollPicker
              value={tempDate}
              onChange={handlePickerChange}
              yearOptional={yearOptional}
              yearRequired={yearRequired}
            />

            {/* Done button */}
            <Button
              onClick={handleDone}
              className="mt-4 w-full rounded-full bg-[hsl(0_0%_10.2%)] text-white hover:bg-[hsl(0_0%_10.2%)]/90"
              size="lg"
            >
              Done
            </Button>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default DatePickerField;
